import React, { FC, useEffect, useState } from 'react';
import { Modal, Skeleton, Empty, Button, Row, Col, Spin } from 'antd';
import { ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import MarkdownIt from 'markdown-it';
import { useDispatch } from 'react-redux';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { ZoneProgressBar } from '../replacement_index_card/ZoneProgressBar';
import {
	CONFIDENCE_TEXT_COLOR,
	CostsTile,
	ReliabilityTile,
	UsefulLifeTile,
	confidenceDisplayLabel,
} from '../replacement_index_card/DimensionTile';
import AIIcon from '../AIIcon';
import {
	AssetDeepAnalysisHistoryRow,
	DeepAnalysisCreatedByContact,
	Recommendation,
} from '../../types/asset_replacement_score';
import { assetDeepAnalysisCreate, assetDeepAnalysisHistoryGet } from '../../thunks/assets_thunks';
import { withUserType } from '../withUserType';
import DateTimeHover from '../date_time_component/DateTime';

interface DeepAnalysisModalProps {
	visible: boolean;
	onClose: () => void;
	assetId: number | string;
	userType: string;
	timeZoneId?: string;
}

const INDEX_LABEL: Record<Exclude<Recommendation, 'insufficient_data'>, string> = {
	repair: 'Low',
	monitor: 'Medium',
	replace: 'High',
};

const INDEX_TAG_CLASS: Record<Exclude<Recommendation, 'insufficient_data'>, string> = {
	repair: 'bg-green-1 text-green-700',
	monitor: 'bg-orange-50 text-orange-600',
	replace: 'bg-red-50 text-red-600',
};

const RUNNING_MESSAGES = [
	'Pulling work order history...',
	'Reviewing cost patterns and spend trends...',
	'Comparing reliability against peer assets...',
	'Evaluating remaining useful life...',
	'Scoring repair-vs-replace tradeoffs...',
	'Drafting recommendation...',
	'Finalizing report...',
];
const RUNNING_MESSAGE_INTERVAL_MS = 4000;

const RunningAnalysisStatus: FC = () => {
	const [messageIndex, setMessageIndex] = useState(0);

	useEffect(() => {
		const intervalId = setInterval(() => {
			setMessageIndex((previous) =>
				previous + 1 < RUNNING_MESSAGES.length ? previous + 1 : previous
			);
		}, RUNNING_MESSAGE_INTERVAL_MS);
		return () => clearInterval(intervalId);
	}, []);

	return (
		<div className="flex min-h-[260px] flex-col items-center justify-center gap-m py-l text-center">
			<Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
			<div className="h5medium text-character-primary">Running Deep Analysis</div>
			<div
				key={messageIndex}
				className="bodyregular text-character-secondary"
				style={{ animation: 'deepAnalysisFadeIn 400ms ease-in' }}
			>
				{RUNNING_MESSAGES[messageIndex]}
			</div>
			<div className="footnoteregular text-character-secondary">
				This can take up to a minute. Feel free to keep this window open.
			</div>
			<style>{`@keyframes deepAnalysisFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
		</div>
	);
};

const markdownRenderer = new MarkdownIt({ html: false, linkify: true, breaks: true });

const LLM_MARKDOWN_CLASSES = [
	'bodyregular',
	'text-character-primary',
	// First child has no top margin so the block doesn't open with a gap
	'[&>*:first-child]:mt-0',
	// Headings — sized down to fit inside the modal. `!` overrides global
	// typography.less rules that set `h1..h6 { font-size: ... !important }`.
	'[&_h1]:!text-[20px] [&_h1]:!leading-[28px] [&_h1]:!font-medium [&_h1]:mt-l [&_h1]:mb-s',
	'[&_h2]:!text-[16px] [&_h2]:!leading-[24px] [&_h2]:!font-medium [&_h2]:mt-m [&_h2]:mb-xs',
	'[&_h3]:!text-[14px] [&_h3]:!leading-[22px] [&_h3]:!font-bold [&_h3]:mt-m [&_h3]:mb-xs',
	'[&_h4]:!text-[14px] [&_h4]:!leading-[22px] [&_h4]:!font-medium [&_h4]:mt-s [&_h4]:mb-xs',
	'[&_h5]:!text-[14px] [&_h5]:!leading-[22px] [&_h5]:!font-medium [&_h5]:mt-s [&_h5]:mb-xs',
	'[&_h6]:!text-[14px] [&_h6]:!leading-[22px] [&_h6]:!font-medium [&_h6]:mt-s [&_h6]:mb-xs',
	// Paragraphs and lists
	'[&_p]:my-s',
	'[&_ul]:my-s [&_ul]:pl-l',
	'[&_ol]:my-s [&_ol]:pl-l',
	'[&_li]:my-xxs',
	// Inline formatting
	'[&_strong]:font-medium',
	'[&_a]:text-purple-7 [&_a]:underline',
	// Code
	'[&_code]:rounded [&_code]:bg-grey-3 [&_code]:px-xxs',
	'[&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-grey-3 [&_pre]:p-s',
	// Tables
	'[&_table]:my-s [&_table]:w-full [&_table]:border-collapse',
	'[&_th]:border [&_th]:border-solid [&_th]:border-grey-4 [&_th]:bg-grey-3 [&_th]:px-s [&_th]:py-xs [&_th]:text-left [&_th]:font-medium',
	'[&_td]:border [&_td]:border-solid [&_td]:border-grey-4 [&_td]:px-s [&_td]:py-xs',
	// Horizontal rule and blockquote
	'[&_hr]:my-m [&_hr]:border-0 [&_hr]:border-t [&_hr]:border-solid [&_hr]:border-grey-4',
	'[&_blockquote]:my-s [&_blockquote]:border-0 [&_blockquote]:border-l-4 [&_blockquote]:border-solid [&_blockquote]:border-grey-4 [&_blockquote]:pl-s [&_blockquote]:text-character-secondary',
].join(' ');

const hasContactName = (contact: DeepAnalysisCreatedByContact | null): boolean =>
	Boolean(contact && (contact.nameGiven || contact.nameFamily));

const avatarContactFor = (entry: AssetDeepAnalysisHistoryRow) => {
	if (hasContactName(entry.createdByContact)) {
		return entry.createdByContact;
	}
	return { nameGiven: entry.createdBy || '', nameFamily: '', photoLink: null };
};

const displayNameFor = (entry: AssetDeepAnalysisHistoryRow): string => {
	const contact = entry.createdByContact;
	if (hasContactName(contact)) {
		const given = (contact && contact.nameGiven) || '';
		const family = (contact && contact.nameFamily) || '';
		return `${given} ${family}`.trim();
	}
	return entry.createdBy || '';
};

const ReplacementIndexTag: FC<{ recommendation: Recommendation }> = ({ recommendation }) => {
	if (recommendation === 'insufficient_data') {
		return <span className="bodymedium rounded-3xl bg-grey-1 px-s text-grey-7">Unavailable</span>;
	}
	const key = recommendation as Exclude<Recommendation, 'insufficient_data'>;
	return (
		<span className={`${INDEX_TAG_CLASS[key]} bodymedium rounded-3xl px-s`}>
			{INDEX_LABEL[key]}
		</span>
	);
};

const IndexAndConfidence: FC<{ entry: AssetDeepAnalysisHistoryRow }> = ({ entry }) => {
	const score = entry.repairReplaceScoreResponse;
	return (
		<>
			<div className="bodyregular flex items-center gap-xs text-character-primary">
				<span>Replacement Index:</span>
				<ReplacementIndexTag recommendation={score.recommendation} />
			</div>
			<div className="bodyregular flex items-center gap-xs text-character-primary">
				<span>Confidence:</span>
				<span className={`bodymedium ${CONFIDENCE_TEXT_COLOR[score.confidence.label]}`}>
					{confidenceDisplayLabel(score.confidence.label)}
				</span>
			</div>
		</>
	);
};

const DeepAnalysisRow: FC<{
	entry: AssetDeepAnalysisHistoryRow;
	onView: (entry: AssetDeepAnalysisHistoryRow) => void;
	isFirst: boolean;
	timeZoneId?: string;
}> = ({ entry, onView, isFirst, timeZoneId }) => {
	const handleView = () => onView(entry);
	const score = entry.repairReplaceScoreResponse;

	return (
		<div
			className={`flex items-center gap-m ${
				isFirst ? '' : 'border-0 border-t border-solid border-grey-4'
			}`}
		>
			<div className="flex-1">
				<div className="flex items-center gap-xs">
					<span className="bodyregular text-character-secondary">Analysis run by:</span>
					<ContactAvatar contact={avatarContactFor(entry)} size={24} />
					<span className="bodymedium text-character-primary">{displayNameFor(entry)}</span>
					<span className="bodyregular text-character-secondary">
						·{' '}
						<DateTimeHover
							timestamp={entry.createdAt}
							dateFormat={'MMM DD h:mma z'}
							showDate
							timeZone={timeZoneId}
						/>
					</span>
				</div>

				<div className="mt-s flex items-center gap-m">
					<IndexAndConfidence entry={entry} />
				</div>

				<ZoneProgressBar
					markerPosition={score.markerPosition}
					bands={score.bands}
					confidence={score.confidence.label}
					labels={['Low', 'Medium', 'High']}
				/>
			</div>

			<div className="flex-none">
				<Button onClick={handleView}>View Analysis</Button>
			</div>
		</div>
	);
};

const DeepAnalysisDetail: FC<{ entry: AssetDeepAnalysisHistoryRow }> = ({ entry }) => {
	const score = entry.repairReplaceScoreResponse;
	const emergencyCount = score.urgency ? score.urgency.emergencyCount : 0;

	return (
		<div>
			<div className="-mx-l -mt-l mb-m bg-grey-2 px-l py-s">
				<span className="bodyregular text-character-secondary">
					Data below was captured at the time of analysis
				</span>
			</div>

			<div className="flex items-center justify-between gap-m">
				<div className="flex items-center gap-m">
					<IndexAndConfidence entry={entry} />
				</div>
				{emergencyCount > 0 ? (
					<div className="bodyregular text-character-secondary">
						{emergencyCount} emergency WOs during time of analysis
					</div>
				) : null}
			</div>

			<ZoneProgressBar
				markerPosition={score.markerPosition}
				bands={score.bands}
				confidence={score.confidence.label}
				labels={['Low', 'Medium', 'High']}
			/>

			<Row className="mt-l items-stretch" gutter={16}>
				<Col xs={24} md={8}>
					<CostsTile dim={score.dimensions.costPressure} />
				</Col>
				<Col xs={24} md={8}>
					<ReliabilityTile dim={score.dimensions.downtimeImportance} />
				</Col>
				<Col xs={24} md={8}>
					<UsefulLifeTile dim={score.dimensions.remainingLife} />
				</Col>
			</Row>

			<div className="mt-l min-h-[220px] rounded-md bg-grey-2 px-l py-l">
				{entry.llmResponse ? (
					<div
						className={LLM_MARKDOWN_CLASSES}
						dangerouslySetInnerHTML={{ __html: markdownRenderer.render(entry.llmResponse) }}
					/>
				) : entry.llmError ? (
					<div className="bodyregular whitespace-pre-wrap text-red-600">{entry.llmError}</div>
				) : (
					<div className="bodyregular flex h-full min-h-[220px] items-center justify-center text-character-secondary">
						No analysis text available
					</div>
				)}
			</div>
		</div>
	);
};

const DeepAnalysisModalInner: FC<DeepAnalysisModalProps> = ({
	visible,
	onClose,
	assetId,
	userType,
	timeZoneId,
}) => {
	const dispatch = useDispatch();
	const [entries, setEntries] = useState<AssetDeepAnalysisHistoryRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [running, setRunning] = useState(false);
	const [selectedEntry, setSelectedEntry] = useState<AssetDeepAnalysisHistoryRow | null>(null);

	const handleBackToList = () => setSelectedEntry(null);
	const handleSelectEntry = (entry: AssetDeepAnalysisHistoryRow) => setSelectedEntry(entry);

	const loadHistory = (): Promise<AssetDeepAnalysisHistoryRow[]> =>
		(dispatch as any)(assetDeepAnalysisHistoryGet(userType)(assetId)).then(
			(result: AssetDeepAnalysisHistoryRow[]) => result || []
		);

	useEffect(() => {
		if (!visible) return;
		let cancelled = false;
		setLoading(true);
		setSelectedEntry(null);
		loadHistory()
			.then((result) => {
				if (cancelled) return;
				setEntries(result);
				setLoading(false);
			})
			.catch(() => {
				if (cancelled) return;
				setEntries([]);
				setLoading(false);
			});
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible, assetId, userType]);

	const handleRunNewAnalysis = () => {
		setRunning(true);
		(dispatch as any)(assetDeepAnalysisCreate(userType)(assetId))
			.then((newEntry: AssetDeepAnalysisHistoryRow) => {
				setEntries((previous) => [newEntry, ...previous]);
				setSelectedEntry(newEntry);
				setRunning(false);
			})
			.catch(() => {
				setRunning(false);
			});
	};

	const listTitle = (
		<div className="flex items-center justify-between pr-l">
			<span>Deep Analysis</span>
			<AccessPermissionChecker name={PERMISSION_NAMES.RUN_DEEP_ANALYSIS}>
				<Button icon={<AIIcon className="mr-2" />} loading={running} onClick={handleRunNewAnalysis}>
					Run New Analysis
				</Button>
			</AccessPermissionChecker>
		</div>
	);

	const detailTitle = selectedEntry ? (
		<div className="flex items-center justify-between pr-l">
			<div className="flex items-center gap-m">
				<Button icon={<ArrowLeftOutlined />} onClick={handleBackToList}>
					Back
				</Button>
				<span>Deep Analysis</span>
			</div>
			<div className="flex items-center gap-xs">
				<span className="bodyregular text-character-secondary">Analysis run by:</span>
				<ContactAvatar contact={avatarContactFor(selectedEntry)} size={24} />
				<span className="bodymedium text-character-primary">{displayNameFor(selectedEntry)}</span>
				<span className="bodyregular text-character-secondary">
					·{' '}
					<DateTimeHover
						timestamp={selectedEntry.createdAt}
						dateFormat={'MMM DD h:mma z'}
						showDate
						timeZone={timeZoneId}
					/>
				</span>
			</div>
		</div>
	) : null;

	return (
		<Modal
			title={selectedEntry ? detailTitle : listTitle}
			visible={visible}
			onCancel={onClose}
			width={selectedEntry ? 920 : 820}
			destroyOnClose
			footer={
				<Button type="primary" onClick={onClose}>
					Done
				</Button>
			}
		>
			{running ? (
				<RunningAnalysisStatus />
			) : loading ? (
				<Skeleton active paragraph={{ rows: 6 }} />
			) : selectedEntry ? (
				<DeepAnalysisDetail entry={selectedEntry} />
			) : entries.length === 0 ? (
				<Empty description="No previous analyses" />
			) : (
				<div>
					{entries.map((entry, index) => (
						<DeepAnalysisRow
							key={entry.id === null ? `entry-${index}` : String(entry.id)}
							entry={entry}
							onView={handleSelectEntry}
							isFirst={index === 0}
							timeZoneId={timeZoneId}
						/>
					))}
				</div>
			)}
		</Modal>
	);
};

export const DeepAnalysisModal = withUserType(DeepAnalysisModalInner) as FC<
	Omit<DeepAnalysisModalProps, 'userType'>
>;

export default DeepAnalysisModal;
